import React, { useEffect, useState } from 'react';
import { Checkbox } from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { Button, DataGridButton, SubTitle, UserDeleteOptionsGreen, UserDeleteOptionsRed } from './styles';
import Modal from '../../../Components/Modal';
import CreateActivationCode from './Create';
import UsedByList from './List';

interface Props {
  id: string;
  type: string;
  version: string;
}

interface TableData {
  name: string;
  code: string;
  isActive: string;
  createdBy: string;
  codeExpiresAt: Date;
  usedBy: string[];
}

const ActivationCodesView: React.FC<Props> = (props) => {
  const [showUsedByList, setShowUsedByList] = useState(false);
  const [showCreateActivationCode, setShowCreateActivationCode] = useState(false);
  const [tableData, setTableData] = React.useState<TableData[]>([]);
  const [codeID, setCodeID] = React.useState("");

  const formatDate = (d: Date) => {
    return [d.getMonth() + 1, d.getDate(), d.getFullYear()].join("/") +
      " " +
      [String(d.getHours()).padStart(2, "0"), String(d.getMinutes()).padStart(2, "0")].join(":");
  }

  const columns: any[] = [
    {
      field: 'name', headerName: 'Name', width: 200,
    },
    {
      field: 'code', headerName: 'Key', width: 240
    },
    {
      field: 'isActive', headerName: 'Active', width: 100,
      renderCell: (params: any) => <span>
        <Checkbox
          {...{
            inputProps: { "aria-label": "Checkbox demo" },
          }}
          size="small"
          checked={params.value}
          onChange={function (event: React.ChangeEvent<HTMLInputElement>) {
            enableActivationCode({
              code: params.row.code,
              isActive: !params.value,
            });
          }}
        />
      </span>
    },
    {
      field: 'createdBy', headerName: 'Created by', width: 180
    },
    {
      field: 'createdAt', headerName: 'Created at', width: 240,
      renderCell: (params: any) => {
        return <span>{formatDate(new Date(params.value))}</span>;
      },
    },
    {
      field: 'codeExpiresAt', headerName: 'Expires at', width: 240,
      renderCell: (params: any) => {
        if (params.value === null) 
          return <span>Never</span>;

        const date = new Date(params.value);
        
        return (
          <span style={{ color: date.getTime() < new Date().getTime() ? 'red' : undefined }}>
            {formatDate(new Date(params.value))}
          </span>
        );
      },
    },
    {
      field: "action",
      headerName: "Action",
      width: 320,
      renderCell: (params: any) => (
        <span style={{}}>
          <DataGridButton
            onClick={() => {
              setShowUsedByList(true);
              setCodeID(params.row.code);
            }}
          >
            View Users
          </DataGridButton>
          {codeID === params.row.id ? (
            <>
              Delete?{" "}
              <UserDeleteOptionsRed
                onClick={() => {
                  onDelete({code: params.row.code});
                  setCodeID("");
                }}
              >
                Yes
              </UserDeleteOptionsRed>{" "}
              <UserDeleteOptionsGreen
                onClick={() => {
                  setCodeID("");
                }}
              >
                No
              </UserDeleteOptionsGreen>
            </>
          ) : (
            <DataGridButton
              style={{ color: "#DB3A34", border: "2px solid #DB3A34" }}
              onClick={() => {
                setCodeID(params.row.id);
              }}
            >
              Delete
            </DataGridButton>
          )}
        </span>
      ),
    },
  ]

  const onCreateConfirmed = () => {
    getSoftwareActivationCodes();
    setShowCreateActivationCode(false);
  }

  const onDelete = (params: {
    code: string;
  }) => {
    const confirmed = window.confirm("This will remove access for all users who have used this code. Are you sure?");
    if (!confirmed) return;

    fetch("/api/v1/software/type/version/activationCode/remove", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        software: props.id,
        type: props.type,
        version: props.version,
        code: params.code,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success === true) {
          getSoftwareActivationCodes();
        }
      }).catch((err) => console.log("Error while deleting activation code:", err));	
  }

  const enableActivationCode = (params: {
    code: string;
    isActive: boolean;
  }) => {
    fetch("/api/v1/software/type/version/activationCode/enable", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        software: props.id,
        type: props.type,
        version: props.version,
        code: params.code,
        isActive: params.isActive,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success === true) {
          getSoftwareActivationCodes();
        }
      }).catch((err) => console.log("Error while enabling activation code:", err));
  }

  const getSoftwareActivationCodes = () => {
    fetch("/api/v1/software/type/version/activationCode/list", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        software: props.id,
        type: props.type,
        version: props.version,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success === true) {
          setTableData(
            data.codes.map((val: any, index: any) => {
              return {
                id: index,
                name: val.name,
                code: val.code,
                isActive: val.isActive,
                createdBy: val.createdBy,
                codeExpiresAt: val.codeExpiresAt,
                createdAt: val.createdAt,
              };
            })
          );
        }
      }).catch((err) => console.log("Error while fetching software list:", err));
  };

  useEffect(() => {
    getSoftwareActivationCodes();

    const interval = setInterval(() => {
      getSoftwareActivationCodes();
    }, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div>
        <SubTitle style={{ marginTop: "3rem" }}>
          Activation Codes
          <Button
            onClick={() => {
              setShowCreateActivationCode(true);
            }}
          >
            Create new Code
          </Button>
        </SubTitle>
        <div style={{ height: 500, width: '100%', paddingTop: "1rem" }}>
          <DataGrid
            style={{ fontWeight: 900, color: "rgba(55, 73, 87, 0.7)" }}
            rows={tableData}
            columns={columns}
            disableColumnFilter
            disableColumnSelector
            disableDensitySelector
            components={{ Toolbar: GridToolbar }}
            componentsProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 500 },
              },
            }}
          />
        </div>
      </div>
      
      {
        /* Create Activation Code Modal */
      }
      <Modal
        open={showCreateActivationCode}
        onClose={() => {
          setShowCreateActivationCode(false);
        }}
      >
        <CreateActivationCode
          id={props.id}
          type={props.type}
          version={props.version}
          onCreate={onCreateConfirmed}
        />
      </Modal>

      {
        /* Used By List Modal */
      }
      <Modal
        open={showUsedByList}
        onClose={() => {
          setShowUsedByList(false);
          setCodeID("");
        }}
      >
        <UsedByList 
          id={props.id} 
          type={props.type} 
          version={props.version} 
          code={codeID}
        />
      </Modal>
    </>
  )
}

export default ActivationCodesView;